<script>
/* eslint-disable no-console */
import Axios from '_shared_/plugins/http';
import encoders from '_shared_/utils/string-encoders';
import { mapGetters } from 'vuex';
import * as renderHelpers from '_download_/helpers';
import { renderDisclosureRow, renderDisclosureRowGaps } from '_download_/disclosures/renderDisclosures';
import { summaryModule } from '_download_/data-detail/summaryModule';
import { renderAllocationRow, renderAllocationFundRow } from '_download_/data-detail/underlyingFundsModule';
import { returnsAtNavModule } from '_download_/data-detail/returnsAtNavModule';
import { assetMixModule } from '_download_/data-detail/assetMixModule';
import { marketCapModule } from '_download_/data-detail/marketCapModule';
import { fundStatisticsModule } from '_download_/data-detail/fundStatisticsModule';
import { descriptionModule } from '_download_/data-detail/descriptionModule';
import { fundFactsModule } from '_download_/data-detail/fundFactsModule';
import { yieldModule } from '_download_/data-detail/yieldModule';
import { valuationModule } from '_download_/data-detail/valuationModule';
import { portfolioManagementModule } from '_download_/data-detail/portfolioManagementModule';
import { morningstarRatingModule } from '_download_/data-detail/morningstarRatingModule';
import { qualitySummaryModule } from '_download_/data-detail/qualitySummaryModule';
import { averageBreakdownModule } from '_download_/data-detail/averageBreakdownModule';
import { riskMeasuresModule } from '_download_/data-detail/riskMeasuresModule';
import { equitiesBreakdownModule } from '_download_/data-detail/equitiesBreakdownModule';
import { bondsBreakdownModule } from '_download_/data-detail/bondsBreakdownModule';
import { geographicBreakdownModule } from '_download_/data-detail/geographicBreakdownModule';
import underlyingHoldingsModule from '_download_/data-detail/underlyingHoldingsModule';
import { newGeoInvestingModule } from '_download_/data-detail/newGeoInvestingModule';
import { historicalDistributionsModule } from '_download_/data-detail/historicalDistributionsModule';
import { feesModule } from '_download_/data-detail/feesModule';
import { expenseModule } from '_download_/data-detail/expenseModule';

const http = Axios.Axios;
const _ = require('lodash/core');

export default {
	name: 'DownloadMixin',
	data() {
		return {
			anchors: new Map(),
			detailPageData: new Map(),
			statistics: new Set([]),
			definitionStats: new Set([])
		};
	},
	computed: {
		...mapGetters([
			'getApiBase',
			'getDisclosureApiBase',
			'getEnvironment',
			'getFundAttributes',
			'getReturnsAtNav',
			'getReturnsPageData',
			'getPortfolioManagementStatistic'
		]),
		printDisclosureUrl() {
			const apiURL = this.getDisclosureApiBase ?? '';
			const audience = this.$store.state.audience ?? '';
			const feeStructure = this.$store.state.fundAttributes?.fundAttributes?.ShareClass ?? '';
			const fundSymbol = this.fundSymbol ?? '';
			const materialCharacteristic = 'morningstar';
			const stagingFlag = this.$store.state.stagingFlag ?? '';
			const statistics = [...this.statistics];
			const digVersion = 'v2';

			return `${apiURL}/print-disclosures?audience=${audience}&feeStructure=${feeStructure}&materialCharacteristic=${materialCharacteristic}&product=${fundSymbol}&stagingFlag=${stagingFlag}&statistics=${statistics}&digVersion=${digVersion}`;
		},
		definitionDisclosureUrl() {
			const apiURL = this.getDisclosureApiBase ?? '';
			const stagingFlag = this.$store.state.stagingFlag ?? '';
			const definitionStats = [...this.definitionStats];

			return `${apiURL}/definitions?stagingFlag=${stagingFlag}&statistics=${definitionStats}`;
		},
		fundSymbol() {
			return this.getFundAttributes?.FundSymbol ?? '';
		},
		fundCGIndexID() {
			return this.getFundAttributes?.CGIndexID ?? '';
		},
		returnsPageData() {
			return this.getReturnsPageData ?? {};
		},
		downloadFileName() {
			const now = new Date();
			return `insurance series download_report-${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}.xls`;
		}
	},
	beforeMount() {
		this.$EventBus.$on('ExcelData', this.storeModuleData);
	},
	methods: {
		storeModuleData(moduleName, data) {
			if (!this.detailPageData[moduleName]) {
				this.detailPageData.set(moduleName, data);
			} else {
				if (!_.isEqual(this.detailPageData[moduleName], data)) {
					this.detailPageData.delete(moduleName);
					this.detailPageData.set(moduleName, data);
				}
			}
		},
		getUserName() {
			//Gets the username from the nc cookie if it exists
			try {
				const cookies = document.cookie?.split(';') ?? [];
				for (const cookie of cookies) {
					if (cookie?.indexOf('nc=') > -1) {
						const cookieKeyValue = cookie.split('=');
						const cookieValues = decodeURIComponent(cookieKeyValue[1]).split('|');
						for (const value of cookieValues) {
							if (value?.indexOf('username') > -1) {
								const userNameKeyValue = value.split('=');
								return userNameKeyValue[1];
							}
						}
					}
				}
			} catch (e) {
				//Unable to obtain username, return empty string
			}
			return '';
		},
		async downloadDetailPage() {
			await this.$store.dispatch('getAndCommit', {
				endpoint: 'returns-all',
				commitId: 'SET_RETURNS_PAGE',
				dataSet: this.$store.state.stagingFlag ?? '',
				audience: 'AFIS Default'
			});
			let dataString = await this.formatDataForExcel('detail-table', 'Fund Details');
			let disclosures;
			let definitions;
			try {
				disclosures = await http({
					method: 'get',
					url: this.printDisclosureUrl ?? ''
				});
				disclosures = disclosures?.data?.data?.disclosures?.[0]?.slides;
			} catch (err) {
				console.error('disclosure call for Excel failed: ', { message: err.message, stack: err.stack, ...err });
			}
			try {
				definitions = await http({
					method: 'get',
					url: this.definitionDisclosureUrl
				});

			} catch (err) {
				console.error('definitions call for Excel failed: ', { message: err.message, stack: err.stack, ...err });
			}
			dataString = this.formatDisclosures(disclosures, definitions, dataString);
			dataString = this.cleanStringForExcel(dataString);

			const domParser = new DOMParser();
			const excelTable = domParser.parseFromString(dataString, 'text/html');
			this.tablesToExcel(['disclosures-table', 'detail-table'], this.downloadFileName, excelTable);
		},
		tablesToExcel: (() => {
			const uri = 'data:application/vnd.ms-excel;base64,';
			return (tables, filename, excelTable) => {
				const contextWorkBook = {
					ExcelWorksheets: '',
					HTMLWorksheets: '',
					ListWorksheets: ''
				};
				const tableElements = [];
				tables?.forEach(name => {
					tableElements.push(excelTable.getElementById(name));
				});
				tableElements.forEach((el, SheetIndex) => {
					let SheetName = el?.dataset?.sheetname;
					if (!SheetName || SheetName === '') {
						SheetName = `Sheet${SheetIndex}`;
					}
					contextWorkBook.ExcelWorksheets += encoders.refsToLiterals('excel', {
						SheetIndex,
						SheetName
					});
					contextWorkBook.HTMLWorksheets += encoders.refsToLiterals('html', {
						SheetIndex,
						SheetContent: el?.outerHTML
					});
					contextWorkBook.ListWorksheets += encoders.refsToLiterals('list', {
						SheetIndex
					});
				});
				let tableHTML = encoders.refsToLiterals('workbook', contextWorkBook);
				tableHTML = encoders.unicodeToHTML(tableHTML);
				tableHTML = encoders.strToUTF8Arr(tableHTML);
				tableHTML = encoders.base64EncArr(tableHTML);
				const link = document.createElement('A');
				link.href = uri + tableHTML;
				link.download = filename ?? 'Workbook.xls';
				link.target = '_blank';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			};
		})(),
		formatDisclosures(disclosures, definitions, detailPage) {
			//Disclosure variable tokens, which may appear in some disclosures (mainly hardcoded)
			const variableTokens = {
				class: this.getFundAttributes?.ShareClass,
				'Fund name': this.detailPageData.get('Title') ?? ''
			};
			const columnWidths = ['0.6666666667in', '7.34375in']; //Converted from 64px, 705px
			let disclosureString = `<table id="disclosures-table" data-SheetName="Disclosures">`;
			disclosureString += renderHelpers.renderColumnWidths(columnWidths);

			//format the disclosures returned into a flat array
			const formattedDisclosures = [];
			disclosures?.forEach(slide => {
				slide?.disclosures?.forEach(disclosure => {
					const obj = {
						text: disclosure?.language
					};
					if (disclosure?.symbolReceived) {
						//if the disclosure had symbols received, add these keys that help in applying numbers to the superscript and disclosure
						obj.symbolReceived = disclosure.symbolReceived;
						obj.symbolAssigned = disclosure?.symbolAssigned;
					}
					formattedDisclosures.push(obj);
				});
			});
			const numberedDisclosures = formattedDisclosures.filter(val => val.symbolReceived) ?? [];
			//loop through the anchors and
			//check if the disclosures has already had a number applied, and use that number for the statistic
			// else apply a new incremented number to the statistic AND apply that number to the disclosure so they match
			this.anchors?.forEach((val, key) => {
				const superScriptNumbers = [];
				val?.forEach(statistic => {
					numberedDisclosures.forEach(disclosureVal2 => {
						if (disclosureVal2?.symbolReceived?.includes(statistic)) {
							superScriptNumbers.push(disclosureVal2?.symbolAssigned);
						}
					});
				});
				//replace the anchors with the superscript numbers
				const regexp = new RegExp(key, 'g');
				const superscripts = [...new Set(superScriptNumbers)].join(', ');
				detailPage = detailPage.replace(regexp, superscripts);
			});

			//render the disclosures
			formattedDisclosures.forEach((disclosure, index, array) => {
				//Do NOT use null-coalescing/chaining: we want to know if the array[index +1] exists and/or if it has .symbolAssigned....
				const nonNumberToNonNumberGap = !disclosure.symbolAssigned && array[index + 1] && !array[index + 1].symbolAssigned;
				const nonNumberToNumberGap = !disclosure.symbolAssigned && array[index + 1] && array[index + 1].symbolAssigned;
				const numberToNonNumber = disclosure.symbolAssigned && array[index + 1] && !array[index + 1].symbolAssigned;
				disclosure.text = this.filterDisclosureVariableTokens(disclosure?.text, variableTokens);
				disclosureString += renderDisclosureRow(disclosure);
				disclosureString += renderDisclosureRowGaps(nonNumberToNonNumberGap, nonNumberToNumberGap, numberToNonNumber);
			});
			disclosureString += renderHelpers.renderRowGaps(3);
			disclosureString += renderDisclosureRow({
				symbolAssigned: '',
				text: 'Terms and Definitions'
			});
			disclosureString += this.iBalls(definitions);
			disclosureString += `</table>`;
			return disclosureString + detailPage;
		},
		filterDisclosureVariableTokens(disclosure, tokens) {
			const vTokenRegexString = String.raw`\((MOD|SCMC)\)\s*Variable\s*Token`;
			const vTokenRegex = RegExp(vTokenRegexString, 'i'); //do NOT use 'g' to avoid using RegExp global state for RegExp.test()
			if (vTokenRegex.test(disclosure)) {
				for (const [key, value] of Object.entries(tokens)) {
					const findKeyRegex = RegExp(String.raw`${vTokenRegexString}\s*:\s*${key}`, 'i');
					disclosure = findKeyRegex.test(disclosure) ? disclosure.replace(findKeyRegex, value) : disclosure;
				}
			}
			return disclosure;
		},
		formatDataForExcel(id, name) {
			const columnWidths = ['271px', '201px', '201px', '201px', '75px', '80px', '80px', '80px'];
			let dataString = `<table id="${id}" data-SheetName="${name}">`;
			dataString += renderHelpers.renderColumnWidths(columnWidths);
			try {
				//each section below also has try/catch blocks
				dataString += renderHelpers.renderFundDetailTitle(this.detailPageData.get('Title'));
				dataString += this.summarySection();
				dataString += this.returnsSection();
				dataString += this.ratingsAndRiskSection();
				dataString += this.holdingsSection();
				//Prices & Distributions Section
				dataString += this.historicalDistributionsModule();
				//Fees & Expenses Section
				dataString += this.feesModule();
				dataString += this.expenseModule();
			} catch (err) {
				console.error('Formatting data for Excel failed: ', { message: err.message, stack: err.stack, ...err });
			}
			dataString += `</table>`;
			this.definitionStats.add(this.fundCGIndexID);
			return dataString;
		},
		/**
		 * Formats fund objective names with spacing if they contain more than one word (i.e. "EquityIncome" should be "Equity Income")
		 * @param objective string
		 */
		formatFundObjective(objective) {
			const formatted = {
				AssetAllocation: 'Asset Allocation',
				EquityIncome: 'Equity Income',
				GrowthIncome: 'Growth and Income',
				ManagedRisk: 'Managed Risk',
				PortfolioSeries: 'Portfolio Series',
				RetirementTargetDate: 'Retirement Target Date'
			};

			return formatted[objective] ? formatted[objective] : objective;
		},
		summarySection() {
			const summarySectionCalls = [this.summaryModule, this.underlyingFundsModule];
			let summaryString = ``;
			summaryString += renderHelpers.renderRowGaps(2);
			summaryString += renderHelpers.renderSectionHeader('Summary');
			summaryString += renderHelpers.renderRowGaps(1);
			summarySectionCalls.forEach(call => {
				try {
					summaryString += call();
				} catch (err) {
					console.error('Formatting data for Summary Section failed: ', { message: err.message, stack: err.stack, ...err });
				}
			});
			return summaryString;
		},
		summaryModule() {
			let summaryString = '';
			if (this.detailPageData.has('SummaryModule')) {
				const data = this.detailPageData.get('SummaryModule') ?? {};
				const fundAttributes = this.$store.state.fundAttributes?.fundAttributes;
				const summaryRowData = {};
				summaryRowData['summary'] = { label: 'Symbol', data: fundAttributes?.FundSymbol };
				summaryRowData['shareClass'] = { label: 'Share Class', data: fundAttributes?.ShareClass };
				summaryRowData['fundDescription'] = { label: 'Fund Description', data: data?.description };
				summaryRowData['priceAtNav'] = { priceAtNav: data.priceAtNav, asOfDate: data?.navAsOfDate };
				summaryRowData['fundAssets'] = { label: 'Fund Assets (millions)', data: data?.fundAssets };
				summaryRowData['portfolioManagers'] = {
					label: `Portfolio Managers ${this.addSuperscript([this.getPortfolioManagementStatistic], '_portManager_')}`,
					data: data?.managerCount
				};
				summaryRowData['expenseRatio'] = {
					label: `Expense Ratio (Gross/Net %) ${this.addSuperscript(['expRatio'], '_expenseRatio_')}`,
					data: `${data?.expenseRatioPercentGross} /${data?.expenseRatioPercentNet}`
				};
				summaryRowData['fundObjective'] = { label: 'Fund Objective', data: this.formatFundObjective(fundAttributes?.Objective) ?? '' };
				summaryString += summaryModule(summaryRowData);
			}
			return summaryString;
		},
		underlyingFundsModule() {
			let underlyingString = '';
			if (this.detailPageData.has('UnderlyingFundsModule')) {
				const data = this.detailPageData.get('UnderlyingFundsModule') ?? {};
				const objectives = {};
				const allocations = [];
				//group entries by objective
				data?.Data?.forEach(obj => {
					const _underlyingFundObjectiveWeb = obj?.UnderlyingFundObjectiveWeb ?? '';
					objectives[_underlyingFundObjectiveWeb] = objectives?.[_underlyingFundObjectiveWeb] ?? [];
					objectives[_underlyingFundObjectiveWeb].push(obj);
				});
				for (const key of Object.keys(objectives)) {
					const obj = {
						objective: key,
						value: objectives?.[key]?.[0]?.UnderlyingObjectivePct,
						funds: key.toLowerCase()?.includes('cash') ? [] : objectives[key]
					};
					allocations.push(obj);
				}
				allocations.sort((a, b) => a.value < b.value);

				underlyingString += renderHelpers.renderSectionHeaderAsOfDate(
					`Underlying Funds ${this.addSuperscript(['fundAllocationTDUnderlying'], '_underlyingFunds_')}`,
					`As of ${data?.AsOfDate} (updated monthly)`
				);
				const stat = this.getFundAttributes?.Objective?.toLowerCase()?.includes('targetdate') ?
					'fundAllocationTD' :
					'UNDFUND_VIM';
				allocations.forEach(allocation => {
					underlyingString += renderAllocationRow(`${allocation?.objective} ${allocation?.objective?.toLowerCase()?.includes('cash & equivalents in support') ? this.addSuperscript([stat], '_allocations_') : ''}`, allocation?.value);
					allocation?.funds?.forEach(fund => {
						underlyingString += renderAllocationFundRow(fund?.UnderlyingFundName, fund?.UnderlyingFundPct);
					});
				});
				underlyingString += renderHelpers.renderRowGaps(2);
			}
			return underlyingString;
		},
		returnsSection() {
			const returnsSectionCalls = [this.returnsAtNavModule, this.assetMixModule, this.marketCapModule, this.fundStatisticsModule, this.descriptionModule, this.fundFactsModule, this.yieldModule, this.valuationModule, this.portfolioManagementModule];
			let returnsString = ``;

			returnsString += renderHelpers.renderSectionHeader('Returns');
			returnsString += renderHelpers.renderRowGaps(2);
			returnsSectionCalls.forEach(call => {
				try {
					returnsString += call();
				} catch (err) {
					console.error('Formatting data for Returns Section failed: ', { message: err.message, stack: err.stack, ...err });
				}

			});
			return returnsString;
		},
		returnsAtNavModule() {
			const fundAttributes = this.$store.state.fundAttributes?.fundAttributes ?? {};
			const monthly = this.returnsPageData?.returns?.monthly?.find(val => {
				return val?.fundNumber === fundAttributes.FundNumber?.toString() && val?.shareClass === fundAttributes.ShareClass;
			});
			const quarterly = this.returnsPageData?.returns?.quarterly?.find(val => {
				return val?.fundNumber === fundAttributes.FundNumber?.toString() && val?.shareClass === fundAttributes.ShareClass;
			});
			const dates = this.getReturnsPageData?.dates?.[fundAttributes.ShareClass] ?? {};
			const sec30DayYield = this.getReturnsAtNav?.Sec30DayYield ?? {};
			sec30DayYield.excelLabel = `${sec30DayYield?.excelLabel} ${this.addSuperscript(['yield30DAnnualized'], '_secYield30Day_')}`;

			monthly.headers = [
				`YTD ${this.addSuperscript(['NAVYTDDaily'], '_yearToDateMonthEnd_')} (as of ${dates?.DailyPricingAsOfDate})`,
				`1 year ${this.addSuperscript(['NAVCumul1Yr'], '_monthlyOneYear_')}`,
				`3 years ${this.addSuperscript(['NAVAvg3yr'], '_monthlyThreeYear_')}`,
				`5 years ${this.addSuperscript(['NAVAvg5yr'], '_monthlyFiveYear_')}`,
				`10 years  ${this.addSuperscript(['NAVAvg10yr'], '_monthlyTenYear_')}`,
				`Since Fund Inception ${this.addSuperscript(['NAVAvgLifetime'], '_monthlyInception_')} (${monthly?.fundInceptionDate})`
			];
			monthly.indexLabel = `${monthly?.indexName} ${this.addSuperscript([this.fundCGIndexID], '_cgIndex_')}`;
			quarterly.headers = [
				`YTD ${this.addSuperscript(['NAVYTDDaily'], '_yearToDateQuarterEnd_')} (as of ${dates?.DailyPricingAsOfDate})`,
				`1 year ${this.addSuperscript(['NAVCumul1Yr'], '_quarterlyOneYear_')}`,
				`3 years ${this.addSuperscript(['NAVAvg3yr'], '_quarterlyThreeYear_')}`,
				`5 years ${this.addSuperscript(['NAVAvg5yr'], '_quarterlyFiveYear_')}`,
				`10 years  ${this.addSuperscript(['NAVAvg10yr'], '_quarterlyTenYear_')}`,
				`Since Fund Inception ${this.addSuperscript(['NAVAvgLifetime'], '_quarterlyInception_')} (${quarterly?.fundInceptionDate})`
			];
			quarterly.indexLabel = `${quarterly?.indexQtrName} ${this.addSuperscript([quarterly?.indexQtrId], '_cgIndex_')}`;
			this.definitionStats.add(quarterly?.indexQtrId);
			this.definitionStats.add('NAV');
			return returnsAtNavModule(monthly, quarterly, dates, sec30DayYield);
		},
		assetMixModule() {
			let assetMixString = '';
			if (this.detailPageData.has('AssetMixModule')) {
				const data = this.detailPageData.get('AssetMixModule') ?? {};
				data.sectionLabel = `Asset Mix ${this.addSuperscript(['assetMix'], '_assetMix_')}`;
				data.assetMix = data?.assetMix?.map(asset => {
					return {
						...asset,
						assetTypeLabel: asset?.assetType?.toLowerCase().includes('cash') ?
							`${asset?.assetType} ${this.addSuperscript(['assetMixCE'], '_assetMixCash_')}` :
							asset?.assetType
					};
				}) ?? [];
				assetMixString += assetMixModule(data);
			}
			return assetMixString;
		},
		marketCapModule() {
			let marketCapString = '';
			if (this.detailPageData.has('MarketCapModule')) {
				const data = this.detailPageData.get('MarketCapModule') ?? {};
				if (data.composite) data.composite.label = `${data?.composite?.label} ${this.addSuperscript(['mktCapitalizationAvg'], '_marketCapAverage_')}` ?? '';
				marketCapString += marketCapModule(data);
			}
			return marketCapString;
		},
		fundStatisticsModule() {
			let fundStatisticsString = '';
			if (this.detailPageData.has('FundStatisticsModule')) {
				const data = this.detailPageData.get('FundStatisticsModule') ?? {};
				data.data = data?.data?.map(stat => {
					if (stat?.label?.toLowerCase()?.includes('life')) this.definitionStats.add('BS_AL');
					if (stat?.label?.toLowerCase()?.includes('duration')) this.definitionStats.add('BS_ED');
					if (stat?.label?.toLowerCase()?.includes('maturity')) this.definitionStats.add('BS_YLDMAT');
					if (stat?.label?.toLowerCase()?.includes('worst')) this.definitionStats.add('BS_YTW');
					if (stat?.label?.toLowerCase()?.includes('coupon')) this.definitionStats.add('AVGCOUP');
					return {
						...stat,
						label: stat?.label?.toLowerCase()?.includes('life') ?
							`${stat?.label} ${this.addSuperscript(['avgLife'], '_averageLife_')}` :
							stat?.label
					};
				}) ?? [];
				fundStatisticsString += fundStatisticsModule(data);
			}
			return fundStatisticsString;
		},
		descriptionModule() {
			let descriptionText = '';
			if (this.detailPageData.has('descriptionModule')) {
				let data = this.detailPageData.get('descriptionModule') ?? [];
				data = data?.map(description => {
					return {
						...description,
						label: description?.label?.toLowerCase()?.includes('distinguishing') ?
							`${description?.label} ${this.addSuperscript(['DC_SB'], '_distinguishingCharacteristics_')}` :
							description?.label
					};
				}) ?? [];
				descriptionText += descriptionModule(data);
			}
			return descriptionText;
		},
		fundFactsModule() {
			let fundFactsText = '';
			if (this.detailPageData.has('FundFactsModule')) {
				let data = this.detailPageData.get('FundFactsModule') ?? {};
				data = data?.leftData.concat(data?.rightData) ?? [];
				data = data?.map(fact => {
					let date = '';
					let superScript = '';
					if (fact?.label === 'Fund Assets (millions)') date = `${fact?.asOfDate} (updated monthly)` ?? '';
					if (fact?.label === 'Companies/Issuers') date = fact?.asOfDate;
					if (fact?.label?.toLowerCase()?.includes('dividends')) superScript = this.addSuperscript(['mthDividendsPaid'], '_monthlyDividend_');
					if (fact?.label?.toLowerCase()?.includes('capital')) superScript = this.addSuperscript(['mthCapitalGainsPaid'], '_capitalGainsPaid_');
					fact.date = date;
					fact.superScript = superScript;
					return fact;
				}) ?? [];
				fundFactsText += fundFactsModule(data);
			}
			return fundFactsText;
		},
		yieldModule() {
			let yieldText = '';
			if (this.detailPageData.has('YieldModule')) {
				const data = this.detailPageData.get('YieldModule') ?? {};
				data.date = `As of ${data?.asOfDate} (updated monthly)` ?? '';
				data.headers = [
					`12-Month Distribution Rates ${this.addSuperscript(['distRate12M'], '_twelveMonthDistribution_')}`,
					`30-day SEC Yield (Gross)`,
					`30-day SEC Yield (Net)`
				];
				yieldText += yieldModule(data);
			}
			return yieldText;
		},
		valuationModule() {
			let valuationText = '';
			if (this.detailPageData.has('ValuationModule')) {
				const data = this.detailPageData.get('ValuationModule') ?? {};
				data.headers = [
					'Price/Book',
					'Price/Cash Flow',
					'Price/Earnings'
				];
				data.cgIndexName = `${data?.cgIndexName} ${this.addSuperscript([this.fundCGIndexID], '_cgIndex_')}` ?? '';
				valuationText += valuationModule(data);

				this.definitionStats.add('PBRAT');
				this.definitionStats.add('PCRAT');
				this.definitionStats.add('PERAT');
			}
			return valuationText;
		},
		portfolioManagementModule() {
			let managementText = '';
			if (this.detailPageData.has('PortfolioManagementModule')) {
				const data = this.detailPageData.get('PortfolioManagementModule') ?? {};
				const title = `${data?.title} ${this.addSuperscript([this.getPortfolioManagementStatistic], '_portManager_')}` ?? '';
				managementText += portfolioManagementModule(data, title);
			}
			return managementText;
		},
		ratingsAndRiskSection() {
			const ratingsAndRiskSectionCalls = [this.morningstarRatingModule, this.qualitySummaryModule, this.averageBreakdownModule, this.riskMeasuresModule];
			let sectionString = ``;
			ratingsAndRiskSectionCalls.forEach(call => {
				try {
					sectionString += call();
				} catch (err) {
					console.error('Rendering Ratings & Risk for Excel failed: ', { message: err.message, stack: err.stack, ...err });
				}
			});
			return sectionString;
		},
		morningstarRatingModule() {
			let morningstarText = '';
			if (this.detailPageData.has('MorningstarRatingModule')) {
				const data = this.detailPageData.get('MorningstarRatingModule') ?? {};
				data.moduleHeader = `Morningstar Rating&trade; ${this.addSuperscript(['morningstarRating'], '_morningstarRating_')}`;
				data.mstarCategoryLabel = `Category`;
				morningstarText += morningstarRatingModule(data);
				this.definitionStats.add('MorningstarCategory');
			}
			return morningstarText;
		},
		qualitySummaryModule() {
			let qualitySummaryText = '';
			if (this.detailPageData.has('QualitySummaryModule')) {
				let data = this.detailPageData.get('QualitySummaryModule') ?? [];
				const title = `Quality Summary  ${this.addSuperscript(['qualitySummary'], '_qualitySummary_')}`;
				const asOfDate = `% of net assets as of ${data?.[0]?.DataAsOfDate ?? '--'} (updated quarterly)`;
				data = data?.map(rating => {
					return {
						...rating,
						label: rating?.SubTypeName?.toLowerCase()?.includes('cash') ?
							`${rating?.SubTypeName}${this.addSuperscript(['fixedIncomeCash'], '_fixedIncomeCash_')}` :
							rating?.SubTypeName
					};
				}) ?? [];
				qualitySummaryText += qualitySummaryModule(title, asOfDate, data);
			}
			return qualitySummaryText;
		},
		holdingsSection() {
			const holdingsSectionCalls = [this.equitiesBreakdownModule, this.bondsBreakdownModule, this.underlyingHoldingsModule, this.geographicBreakdownModule, this.newGeoInvestingModule];
			let holdingsString = ``;
			//Render section header
			holdingsString += renderHelpers.renderRowGaps(1);
			holdingsString += renderHelpers.renderSectionHeader(`Holdings ${this.addSuperscript(['TTLHLD'], '_holdings_')}`);
			holdingsString += renderHelpers.renderRowGaps(1);
			//Render modules
			holdingsSectionCalls.forEach(call => {
				try {
					holdingsString += call();
				} catch (err) {
					console.error('Formatting data for Holdings Section failed: ', { message: err.message, stack: err.stack, ...err });
				}

			});
			return holdingsString;
		},
		averageBreakdownModule() {
			let breakdownText = '';
			if (this.detailPageData.has('AverageBreakdownModule')) {
				const data = this.detailPageData.get('AverageBreakdownModule') ?? [];
				const asOfDateString = `% of net assets as of ${data?.[0]?.DataAsOfDate ?? '--'} (updated quarterly).`;
				breakdownText += averageBreakdownModule(asOfDateString, data);
			}
			return breakdownText;
		},
		riskMeasuresModule() {
			let riskText = '';
			if (this.detailPageData.has('RiskMeasuresModule')) {
				const data = this.detailPageData.get('RiskMeasuresModule') ?? {};
				data.standardDeviationLabel = `Standard Deviation ${this.addSuperscript(['stdDeviation'], '_standardDeviation_')}`;
				data.sharpeRatioLabel = `Sharpe Ratio ${this.addSuperscript(['sharpe'], '_sharpeRatio_')}`;
				//(checking for data.americanFundsBenchMark & data.morningstarBenchmark to avoid TypeError assigning sub-keys to undefined data.key
				if (data.americanFundsBenchMark && data.isMultipleIndex) {
					//AF labels, if data is multiple index
					data.americanFundsBenchMark.indexLabel = data?.americanFundsBenchMark?.index;
					data.americanFundsBenchMark.rSquaredLabel = `R-squared`;
					data.americanFundsBenchMark.betaLabel = `Beta`;
					data.americanFundsBenchMark.captureRatioDownLabel = `Capture Ratio (Downside)`;
					data.americanFundsBenchMark.captureRatioUpLabel = `Capture Ratio (Upside)`;
				}
				if (data.morningstarBenchmark) {
					//Morningstar labels and statistic tags
					data.morningstarBenchmark.indexLabel = `${data?.morningstarBenchmark?.index}`;
					data.morningstarBenchmark.rSquaredLabel = `R-squared`;
					data.morningstarBenchmark.betaLabel = `Beta`;
					data.morningstarBenchmark.captureRatioDownLabel = `Capture Ratio (Downside)`;
					data.morningstarBenchmark.captureRatioUpLabel = `Capture Ratio (Upside)`;
				}
				data.fundCGIndexID = this.fundCGIndexID;
				data.addSuperscript = this.addSuperscript;
				riskText += riskMeasuresModule(data);

				if (data.isMultipleIndex) this.definitionStats.add('MB_STRATEGY');
				this.definitionStats.add('SDF');
				this.definitionStats.add('SRAT');
				this.definitionStats.add('RSQ_AFR');
				this.definitionStats.add('BETA_AF');
				this.definitionStats.add('DCAPRAT');
				this.definitionStats.add('UCAPRAT');
			}
			return riskText;
		},
		equitiesBreakdownModule() {
			let equitiesText = '';
			if (this.detailPageData.has('EquitiesBreakdownModule')) {
				const data = this.detailPageData.get('EquitiesBreakdownModule') ?? {};
				data.topEquitiesHeader = `Top Equities${data?.topEquities?.length ? this.addSuperscript(['TopHLD'], '_topHolding_') : ''}`;
				equitiesText += equitiesBreakdownModule(data);
			}
			return equitiesText;
		},
		bondsBreakdownModule() {
			let bondsText = '';
			if (this.detailPageData.has('BondsBreakdownModule')) {
				this.definitionStats.add('BS_OAS');
				const data = this.detailPageData.get('BondsBreakdownModule') ?? {};
				if (data.bonds) {
					data.bonds.data = data?.bonds?.data?.map(holding => {
						return {
							...holding,
							label: holding?.name?.toLowerCase().includes('cash') ?
								`${holding?.name} ${this.addSuperscript(['PS_CE'], '_bondCash_')}` :
								holding?.name
						};
					}) ?? [];
				}
				data.topIssuersLabel = `Top Fixed-Income Issuers ${this.addSuperscript(['TopHLD'], '_topFixedIncome_')}`;
				bondsText += bondsBreakdownModule(data);
			}
			return bondsText;
		},
		underlyingHoldingsModule() {
			let underlyingHoldingsString = '';
			if (this.detailPageData.has('UnderlyingFundsModule')) {
				const data = this.detailPageData.get('UnderlyingFundsModule') ?? {};
				const objectives = [];
				const allocations = [];

				//Group entries by objective
				data?.Data?.forEach(obj => {
					const _underlyingFundObjectiveWeb = obj?.UnderlyingFundObjectiveWeb ?? '';
					objectives[_underlyingFundObjectiveWeb] = objectives?.[_underlyingFundObjectiveWeb] ?? [];
					objectives[_underlyingFundObjectiveWeb].push(obj);
				});
				//Save an obj for each objective containing the object and funds
				for (const key of Object.keys(objectives)) {
					const obj = {
						objective: key,
						funds: key?.toLowerCase()?.includes('cash') ? [] : objectives[key]
					};
					allocations.push(obj);
				}
				underlyingHoldingsString += underlyingHoldingsModule(allocations);
			}
			return underlyingHoldingsString;
		},
		geographicBreakdownModule() {
			let geographicText = '';
			if (this.detailPageData.has('GeographicBreakdownModule')) {
				const data = this.detailPageData.get('GeographicBreakdownModule') ?? {};
				data.regionsData = data?.regionsData?.map(region => {
					return {
						...region,
						label: region?.SubTypeName?.toLowerCase()?.includes('cash') ?
							`${region?.SubTypeName} ${this.addSuperscript(['ALLCTRY_CECTRY'], '_geographicCash_')}` :
							region?.SubTypeName
					};
				}) ?? [];
				geographicText += geographicBreakdownModule(data);
			}
			return geographicText;
		},
		newGeoInvestingModule() {
			let newGeoText = '';
			if (this.detailPageData.has('NewGeoInvestingModule')) {
				const data = this.detailPageData.get('NewGeoInvestingModule') ?? {};
				data.sectionLabel = `The New Geography of Investing&reg; ${this.addSuperscript(['NEWGEO'], '_newGeography_')}`;
				data.indexLabel = `${data.indexName} ${this.addSuperscript([this.fundCGIndexID], '_cgIndex_')}`;
				data.strategyByDomicile = data?.strategyByDomicile?.map(region => {
					return {
						...region,
						RegionLabel: region?.RegionName?.toLowerCase()?.includes('cash') ?
							`${region?.RegionName} ${this.addSuperscript(['CBD_CE'], '_fundBreakdownCash_')}` :
							region?.RegionName
					};
				}) ?? [];
				newGeoText += newGeoInvestingModule(data);
			}
			return newGeoText;
		},
		historicalDistributionsModule() {
			let histDistText = '';
			if (this.detailPageData.has('HistoricalDistributionsModule')) {
				const data = this.detailPageData.get('HistoricalDistributionsModule') ?? {};
				histDistText += historicalDistributionsModule(data);
				this.definitionStats.add('NON_DAILY_DIV');
				this.definitionStats.add('NON_DAILY_SP_DIV');
				this.definitionStats.add('CAP_GAIN_PAID');
				this.definitionStats.add('ST_CAP_GAIN_PAID');
			}
			return histDistText;
		},
		feesModule() {
			let feesText = '';
			if (this.detailPageData.has('FeesModule')) {
				const data = this.detailPageData.get('FeesModule') ?? {};
				feesText += feesModule(data);
			}
			return feesText;
		},
		expenseModule() {
			let expenseText = '';
			if (this.detailPageData.has('ExpenseModule')) {
				const data = this.detailPageData.get('ExpenseModule') ?? {};
				data.sectionLabel = `Expense Ratio ${this.addSuperscript(['expRatio'], '_moduleExpenseRatio_')}`;
				expenseText += expenseModule(data);
			}
			return expenseText;
		},
		cleanStringForExcel(text) {
			// replace any string starting with 'null' with double-dash
			text = text.replace(/null*/g, '--');
			//replace 'NaN' with double-dash
			text = text.replace(/NaN/g, '--');
			//replace whitespace with space
			text = text.replace(/\s/g, ' ');
			//replace Microsoft quote mark with regular quote mark
			text = text.replace(/’/g, "'");
			//strip a tags because excel does not handle links well
			text = text.replace(/<a[^>]*>/g, '').replace(/<\/a>/g, '');
			//just in case the ?. chaining returns undefined
			text = text.replace(/undefined/g, '');
			return text;
		},
		/**
		 * Adds statistic(s) and anchor tags to the Mixin's "statistics" state, and returns in superscript. Later resolved into superscript numbers,
		 * if a number exists
		 * @param statistics
		 * @param anchor
		 */
		addSuperscript(statistics, anchor) {
			let numbers = '';
			for (const stat of statistics) {
				if (numbers) numbers += ' ';
				this.statistics.add(stat);
				numbers += [...this.statistics].indexOf(stat) + 1;
			}

			this.anchors.set(anchor, statistics);
			//Use the following to put statistics in superscripts instead for debugging: return `<sup>${anchor}. ${statistics[0]}</sup>`;
			return `<sup>${anchor}</sup>`;
		},
		iBalls(definitions) {
			let definitionsTable = ``;
			try {
				definitions = definitions?.data?.data?.definitions;
				if (this.definitionStats.has('MB_STRATEGY')) {
					let strategyDefinition = '';
					const data = this.detailPageData.get('RiskMeasuresModule') ?? {};
					strategyDefinition = data?.MB_STRATEGY;
					definitions.push({
						title: data?.morningstarBenchmark?.indexLabel,
						definition: strategyDefinition
					});
				}
				definitions = definitions.sort((a, b) => { return a.title[0] < b.title[0] ? -1 : 1; });
				definitions.forEach(val => {
					let title = val?.title?.replace('-- from FAMIS', '') ?? '';
					title = title?.replace('-- from MOD', '');
					title = title?.trim();
					const input = {
						symbolAssigned: '',
						text: `${title}: ${val?.definition}`
					};
					definitionsTable += renderDisclosureRow(input);
				});
			} catch (err) {
				console.error('Rendering definitions for Excel failed: ', { message: err.message, stack: err.stack, ...err });
			}
			return definitionsTable;
		}
	}
};
</script>
